import { memo, useCallback, useContext, useMemo, useState } from "react";
import {
  getNodesBounds,
  NodeProps,
  NodeResizer,
  NodeToolbar,
  Position,
  useReactFlow,
  useStore
} from "reactflow";
import { Button, Dimmer, Icon, Loader } from "semantic-ui-react";
import { useDeleteFarmGate } from "../../../api/farmGate";
import { useDeleteProcessGate } from "../../../api/processGate/processGate";
import { nodeCategories, nodeType } from "../../../config/drawingConstants";
import { errorView } from "../../../helpers/ErrorHandler";
import ConfirmModal from "../../confirmViewModal/ConfirmModal";
import { DrawingContext } from "../DrawingTool";

const lineStyle = { borderColor: "transparent" };
const padding = 25;
const backgroundColors = {
  [nodeCategories.farmGate]: "rgba(239, 255, 193, 0.5)",
  [nodeCategories.processGate]: "rgba(216, 255, 243, 0.5)"
};

const GroupNode = ({ id, data, type }: NodeProps) => {
  const { deleteElements } = useReactFlow();
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { mutate: deleteFarmGate, isLoading: loadingFarmGateDelete } = useDeleteFarmGate();

  const { mutate: deleteProcessGate, isLoading: loadingProcessGateDelete } = useDeleteProcessGate();

  const { toggleProcessGateModel, lockStatus } = useContext(DrawingContext);

  const reactFlow = useReactFlow();

  const nodeItem = useMemo(() => reactFlow.getNode(id || ""), [id, data]);

  const { minWidth, minHeight, hasChildNodes, childNodes } = useStore(store => {
    const childNodes = Array.from(store.nodeInternals.values()).filter(n => n.parentNode === id);
    const rect = getNodesBounds(childNodes);
    return {
      minWidth: rect.width + padding * 2,
      minHeight: rect.height + padding * 2,
      hasChildNodes: childNodes.length > 0,
      childNodes
    };
  }, isEqual);

  // Delete Farm gate with validation
  const deleteFarmGateGroup = useCallback(() => {
    const isHaveCultivar = childNodes.some(cn => cn?.data.icon !== nodeType.Cultivar);
    if (isHaveCultivar) {
      errorView("Please delete all items inside the farm gate except cultivar");
      return;
    }
    const farmGateId = data?.reference?.farmGateId;
    if (farmGateId) {
      setDeleteConfirm(true);
      return;
    }
    deleteElements({ nodes: [{ id }] });
  }, [childNodes]);

  // Delete Process gate with validation
  const deleteProcessGateGroup = useCallback(() => {
    if (hasChildNodes) {
      errorView("Please delete all items inside the process gate!");
      return;
    }
    setDeleteConfirm(true);
  }, [hasChildNodes]);

  // Delete Group
  const onDelete = useCallback(() => {
    switch (type) {
      case nodeCategories.farmGate:
        deleteFarmGateGroup();
        break;
      case nodeCategories.processGate:
        deleteProcessGateGroup();
        break;
      case nodeCategories.ingredientGate:
        onDeleteConfirm();
        break;
    }
  }, [type, deleteFarmGateGroup, deleteProcessGateGroup]);

  // Edit process gate
  const onEditProcessGate = useCallback(() => {
    switch (type) {
      case nodeCategories.processGate:
        toggleProcessGateModel(nodeItem, true);
        break;
    }
  }, [type, nodeItem]);

  // Delete confirmation for group
  const onDeleteConfirm = useCallback(() => {
    switch (type) {
      case nodeCategories.farmGate:
        const farmGateId = data.reference.farmGateId;
        deleteFarmGate(farmGateId, {
          onSuccess: () => {
            deleteElements({ nodes: [{ id }] });
          }
        });
        break;
      case nodeCategories.processGate:
        const processGateId = data.reference.processGateId;
        deleteProcessGate(processGateId, {
          onSuccess: () => {
            deleteElements({ nodes: [{ id }] });
          }
        });
        break;
      case nodeCategories.ingredientGate:
        deleteElements({ nodes: [{ id }] });
        break;
    }
  }, [deleteFarmGate, data]);

  const backgroundColor = useMemo(() => backgroundColors[type] || "#E5E7F1", [type]);

  // Loading state check
  if (loadingFarmGateDelete || loadingProcessGateDelete) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <div
        className="drawing_group"
        style={{
          minWidth,
          minHeight,
          width: "100%",
          height: "100%",
          backgroundColor
        }}
      >
        <label>{data.label}</label>
        <NodeResizer
          handleClassName="group_resizer"
          lineStyle={lineStyle}
          minWidth={minWidth}
          minHeight={minHeight}
          isVisible={!lockStatus}
        />
        <NodeToolbar position={Position.Bottom} align="end" offset={5}>
          &nbsp;
          <Button.Group size="tiny">
            {type === nodeCategories.processGate && (
              <Button icon color="blue">
                <Icon
                  name="edit"
                  onClick={() => {
                    onEditProcessGate();
                  }}
                />
              </Button>
            )}
            <Button icon color="red">
              <Icon
                name="close"
                onClick={() => {
                  onDelete();
                }}
              />
            </Button>
          </Button.Group>
        </NodeToolbar>
      </div>

      {deleteConfirm ? (
        <ConfirmModal
          viewModal={deleteConfirm}
          closeModal={() => {
            setDeleteConfirm(false);
          }}
          cancel={() => {
            setDeleteConfirm(false);
          }}
          approve={() => {
            onDeleteConfirm();
          }}
          title={
            nodeCategories.farmGate === type
              ? "Are you sure you want to remove the farm gate?"
              : "Are you sure you want to remove the process gate?"
          }
          subTitle={
            nodeCategories.farmGate === type
              ? "Please consider you cannot recover these data after remove."
              : "Please consider you cannot recover these data after remove."
          }
        />
      ) : null}
    </>
  );
};

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(GroupNode);
